import { useEffect, useRef, useState } from 'react';

import { Box } from '@zillow/constellation-10';

import type React from 'react';

type TransitionWrapperProps = {
  children?: React.ReactNode;
  transitionOpacity?: number;
  transitionDuration?: number;
  isTransitioning?: boolean;
};

const TransitionWrapper = ({
  children,
  isTransitioning = false,
  transitionOpacity = 0.8,
  transitionDuration = 750,
}: TransitionWrapperProps) => {
  const [isTransitioningOut, setIsTransitioningOut] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    if (!isTransitioning) {
      setIsTransitioningOut(true);
      const t = setTimeout(() => {
        if (isMountedRef.current) {
          setIsTransitioningOut(false);
        }
      }, transitionDuration);
      return () => {
        clearTimeout(t);
        isMountedRef.current = false;
      };
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [isTransitioning, transitionDuration]);

  const showOverlay = isTransitioning || isTransitioningOut;

  return (
    <Box
      className="transition-wrapper"
      data-test="transition-wrapper"
      data-test-is-transitioning={showOverlay}
      css={{ position: 'relative', zIndex: 1 }}
    >
      <Box
        data-test="overlay"
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          // oxlint-disable-next-line constellation/no-raw-color-values
          backgroundColor: 'white',
          transition: 'opacity 750ms',
          zIndex: showOverlay ? 1000 : -1,
          opacity: showOverlay ? (isTransitioningOut ? 0 : transitionOpacity) : 0,
        }}
      >
        <Box
          data-test="progress-animation-wrapper"
          css={{
            display: 'table',
            borderSpacing: '10px 10px',
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `padding: 50px` falls between `1200` (48px) and `1300` (52px) on the numeric spacing scale
            padding: '50px',
            '& .progress-dot': {
              display: 'table-cell',
              verticalAlign: 'middle',
              // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 15px` falls between `350` (14px) and `400` (16px) on the numeric sizes scale
              width: '15px',
              // oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 15px` falls between `350` (14px) and `400` (16px) on the numeric sizes scale
              height: '15px',
              borderRadius: '50%',
              // oxlint-disable-next-line constellation/no-raw-color-values
              backgroundColor: '#0077f7',
              animation: 'loadingFade 1s infinite',
              '&:nth-of-type(1)': { animationDelay: '0s' },
              '&:nth-of-type(2)': { animationDelay: '0.12s' },
              '&:nth-of-type(3)': { animationDelay: '0.3s' },
            },
          }}
        >
          <span className="progress-dot" data-test="progress-animation" />
          <span className="progress-dot" data-test="progress-animation" />
          <span className="progress-dot" data-test="progress-animation" />
        </Box>
      </Box>
      {children}
    </Box>
  );
};

export default TransitionWrapper;
